"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import styles from "./Navigation.module.css";

const navData = [
  { name: "Home", href: "/" },
  {
    name: "Products",
    href: "/products",
    megaMenu: {
      featured: {
        title: "Featured Product",
        subtitle: "Premium Paper",
        description: "Eco-friendly, durable, and versatile for all your packaging needs.",
        image: "/logo.png",
        link: "/products"
      },
      categories: [
        {
          title: <span style={{ color: "#FF671B" }}>Paper</span>,
          bgIcon: "roll",
          items: [
            { name: "PE Coated Rolls", href: "/products/pe-coated-rolls", icon: "roll" },
            { name: "Notebook Cover Jumbo Rolls", href: "/products/notebook-cover-jumbo-rolls", icon: "book" },
            { name: "Soap Stiffener Paper", href: "/products/soap-stiffener-paper", icon: "sheet" },
            { name: "Food Wrap Paper", href: "/products/food-wrap-paper", icon: "wrap" },
            { name: "Packaging", href: "/products/packaging", icon: "box" },
            { name: "PE Silver Coated Envelope Paper", href: "/products/pe-silver-coated-envelope-paper", icon: "envelope" },
          ]
        },
        {
          title: <span style={{ color: "#FF671B" }}>Raw Material</span>,
          bgIcon: "cup",
          items: [
            { name: "Paper Cup Sleeves", href: "/products/paper-cup-sleeves", icon: "cup" },
            { name: "Ripple Paper Cup Sleeves", href: "/products/ripple-paper-cup-sleeves", icon: "rippleCup" },
            { name: "Paper Plates", href: "/products/paper-plates", icon: "plate" },
            { name: "Sugar Sachets", href: "/products/sugar-sachets", icon: "sachet" },
            { name: "Pouches & Rolls", href: "/products/pouches-rolls", icon: "pouch" },
            { name: "Cup Bottoms", href: "/products/cup-bottoms", icon: "cupBottom" },
            { name: "Container Sleeves", href: "/products/container-sleeves", icon: "container" },
            { name: "Box & Tray Sleeves", href: "/products/box-tray-sleeves", icon: "tray" },
          ]
        },
      ]
    }
  },
  {
    name: "Services",
    href: "/services",
    megaMenu: {
      categories: [
        {
          title: "Our Services",
          items: [
            { name: "Bulk Supply", href: "/services/bulk-supply", icon: "truck" },
            { name: "Custom Packaging", href: "/services/custom-packaging", icon: "package" },
            { name: "Quality Testing", href: "/services/quality-testing", icon: "check" },
            { name: "Logistics Support", href: "/services/logistics-support", icon: "shipping" },
          ]
        }
      ]
    }
  },
  { name: "About Us", href: "/about" },
  { name: "Contact", href: "/contact" },
];

const Icons = {
  package: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  truck: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  ),
  shipping: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  scissors: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22,4 12,14.01 9,11.01"/>
    </svg>
  ),
  box: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  arrow: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17L17 7M17 7H7M17 7V17" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6,9 12,15 18,9"/>
    </svg>
  ),
  roll: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/>
    </svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/>
    </svg>
  ),
  sheet: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/>
    </svg>
  ),
  wrap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="12" rx="2"/><path d="M7 8V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"/>
    </svg>
  ),
  envelope: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  ),
  cup: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8z"/>
    </svg>
  ),
  rippleCup: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8z"/><path d="M3 11c2 1 4 1 6 0s4-1 6 0"/>
    </svg>
  ),
  plate: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/>
    </svg>
  ),
  sachet: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
    </svg>
  ),
  pouch: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    </svg>
  ),
  cupBottom: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 4h8l2 4v8a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z"/><ellipse cx="12" cy="20" rx="6" ry="1.5"/>
    </svg>
  ),
  container: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="2"/><path d="M4 9h16"/><path d="M9 3v6"/><path d="M15 3v6"/>
    </svg>
  ),
  tray: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
    </svg>
  ),
};

export default function Navigation() {
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const navShellRef = useRef(null);

  const syncNavHeight = useCallback(() => {
    const shell = navShellRef.current;
    if (!shell) return;
    document.documentElement.style.setProperty(
      "--site-nav-height",
      `${shell.offsetHeight}px`
    );
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    syncNavHeight();
    window.addEventListener("resize", syncNavHeight);
    return () => window.removeEventListener("resize", syncNavHeight);
  }, [syncNavHeight, scrolled]);

  useEffect(() => {
    const shell = navShellRef.current;
    if (!shell || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(syncNavHeight);
    observer.observe(shell);
    return () => observer.disconnect();
  }, [syncNavHeight]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const toggleMobile = () => {
    setMobileOpen(!mobileOpen);
    setMobileSubmenu(null);
  };

  const handleCatalogDownload = (e) => {
    // Prevent default navigation
    e.preventDefault();
    
    // Create a catalog download - you can replace this with your actual catalog PDF
    const catalogPath = "/catalog/greeneripapers-catalog.pdf";
    
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = catalogPath;
    link.download = "greeneripapers-catalog.pdf";
    link.target = "_blank";
    
    // Trigger the download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle dropdown item click - close dropdown and scroll to top if same page
  const handleDropdownItemClick = (href, event) => {
    // Close dropdown immediately
    setActiveMenu(null);
    
    // Check if it's the same page
    const currentPath = window.location.pathname;
    const targetPath = href.split('?')[0]; // Remove query params if any
    
    if (currentPath === targetPath) {
      event.preventDefault();
      // Smooth scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Check if current page matches href for active state
  const isCurrentPage = (href) => {
    const currentPath = location.pathname;
    const targetPath = href.split('?')[0];
    if (targetPath === '/') {
      return currentPath === '/';
    }
    return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
  };

  return (
    <>
      <div
        ref={navShellRef}
        className={`${styles.navShell} ${scrolled ? styles.scrolled : ""}`}
      >
      <div className={styles.topBar}>
        <div className={styles.topBarContent}>
          <div className={styles.topBarLeft}>
            <span className={styles.topBarIcon}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
            </span>
            <a href="mailto:info@greeneripapers.com">info@greeneripapers.com</a>
            <span className={styles.topBarDivider}></span>
            <span className={styles.topBarIcon}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
            </span>
            <a href="tel:+919557005492">+91 9557005492</a>
          </div>
          <div className={styles.topBarRight}>
            <span className={styles.marqueeText}>
              <span>Quality Paper Solutions</span>
              <span className={styles.marqueeStar}>&#10038;</span>
              <span>Since 2018</span>
              <span className={styles.marqueeStar}>&#10038;</span>
              <span>Trusted by 500+ Businesses</span>
              <span className={styles.marqueeStar}>&#10038;</span>
              <span>Eco-Friendly Products</span>
              <span className={styles.marqueeStar}>&#10038;</span>
            </span>
          </div>
        </div>
      </div>

      <header
        className={styles.header}
        onMouseLeave={() => setActiveMenu(null)}
      >
        <nav className={styles.nav}>
          <Link to="/" className={styles.logo}>
            <img src="/logo.png" alt="Greeneri Papers" className={styles.logoImage} />
          </Link>

          <div className={styles.navCenter}>
            <div className={styles.desktopNav}>
              {navData.map((item) => (
                <div
                  key={item.name}
                  className={styles.navItem}
                  onMouseEnter={() => setActiveMenu(item.megaMenu ? item.name : null)}
                >
                  <Link
                    to={item.href}
                    className={`${styles.navLink} ${activeMenu === item.name ? styles.active : ""} ${isCurrentPage(item.href) ? styles.current : ""}`}
                  >
                    <span className={styles.navLinkText}>{item.name}</span>
                    {item.megaMenu && <span className={styles.navChevron}>{Icons.chevron}</span>}
                  </Link>

                {/* Mega Menu for Products */}
                {item.megaMenu && item.megaMenu.featured && activeMenu === item.name && (
                  <div className={styles.megaMenu}>
                    <div className={styles.megaMenuInner}>
                      {/* Featured Product (Green Left Section) */}
                      <div className={styles.megaFeatured}>
                        <div className={styles.megaFeaturedBadge}>Featured</div>
                        <div className={styles.megaFeaturedImage}>
                          <img src={item.megaMenu.featured.image} alt="" />
                        </div>
                        <h4>{item.megaMenu.featured.title}</h4>
                        <p>{item.megaMenu.featured.subtitle}</p>
                        <span className={styles.megaFeaturedDesc}>{item.megaMenu.featured.description}</span>
                        <Link to={item.megaMenu.featured.link} className={styles.megaFeaturedLink}>
                          Explore {Icons.arrow}
                        </Link>
                      </div>
                      
                      {/* Categories (White Right Section with Columns) */}
                      <div className={styles.megaCategories}>
                        {item.megaMenu.categories.map((cat, i) => (
                          <div key={i} className={styles.megaCategory} style={{ "--delay": `${i * 0.08}s` }}>
                            {cat.bgIcon && Icons[cat.bgIcon] && (
                              <span className={styles.megaCategoryBgIcon} aria-hidden="true">
                                {Icons[cat.bgIcon]}
                              </span>
                            )}
                            <h5 className={styles.megaCategoryTitle}>{cat.title}</h5>
                            <ul className={styles.megaCategoryList}>
                              {cat.items.map((subItem, j) => (
                                <li key={j} style={{ "--delay": `${0.12 + j * 0.04}s` }}>
                                  <Link 
                                    to={subItem.href} 
                                    className={`${styles.megaCategoryLink} ${isCurrentPage(subItem.href) ? styles.activeLink : ''}`}
                                    onClick={(e) => handleDropdownItemClick(subItem.href, e)}
                                  >
                                    {subItem.icon && Icons[subItem.icon] && (
                                      <span className={styles.megaLinkIcon} aria-hidden="true">
                                        {Icons[subItem.icon]}
                                      </span>
                                    )}
                                    <span className={styles.megaLinkLabel}>{subItem.name}</span>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Services Mega Menu */}
                {item.megaMenu && !item.megaMenu.featured && activeMenu === item.name && (
                  <div className={`${styles.megaMenu} ${styles.megaMenuServices}`}>
                    <div className={styles.megaMenuInner}>
                      <div className={styles.servicesGrid}>
                        {item.megaMenu.categories[0].items.map((service, i) => (
                          <Link 
                            key={i} 
                            to={service.href} 
                            className={`${styles.serviceCard} ${isCurrentPage(service.href) ? styles.activeService : ''}`}
                            style={{"--delay": `${i * 0.05}s`}}
                            onClick={(e) => handleDropdownItemClick(service.href, e)}
                          >
                            <div className={styles.serviceIcon}>
                              {Icons[service.icon]}
                            </div>
                            <span className={styles.serviceName}>{service.name}</span>
                            <span className={styles.serviceArrow}>{Icons.arrow}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>

          <div className={styles.navActions}>
            <Link to="/catalog" className={styles.ctaButton} onClick={handleCatalogDownload}>
              <span className={styles.ctaButtonText}>Download</span>
              <span className={styles.ctaButtonIcon}>{Icons.arrow}</span>
            </Link>

            <button className={`${styles.mobileToggle} ${mobileOpen ? styles.open : ""}`} onClick={toggleMobile} aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen}>
              <span></span><span></span><span></span>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu — outside header so it stacks above sticky nav and scrolls fully */}
      <div className={`${styles.mobileMenu} ${mobileOpen ? styles.open : ""}`} aria-hidden={!mobileOpen}>
        <button
          type="button"
          className={styles.mobileMenuBg}
          aria-label="Close menu"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={toggleMobile}
        />
        <div className={styles.mobileMenuContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.mobileNavList}>
            {navData.map((item, index) => (
              <div
                key={item.name}
                className={styles.mobileNavItem}
                style={{ "--index": index }}
              >
                  {item.megaMenu ? (
                    <>
                      <div className={styles.mobileNavRow}>
                        <Link
                          to={item.href}
                          className={`${styles.mobileNavLink} ${isCurrentPage(item.href) ? styles.mobileNavLinkCurrent : ""}`}
                          onClick={toggleMobile}
                        >
                          <span className={styles.mobileNavNumber}>0{index + 1}</span>
                          <span className={styles.mobileNavText}>{item.name}</span>
                          <span className={styles.mobileNavArrow}>{Icons.arrow}</span>
                        </Link>
                        <button
                          type="button"
                          className={`${styles.mobileNavExpand} ${mobileSubmenu === item.name ? styles.active : ""}`}
                          onClick={() => setMobileSubmenu(mobileSubmenu === item.name ? null : item.name)}
                          aria-expanded={mobileSubmenu === item.name}
                          aria-label={`Show ${item.name} submenu`}
                        >
                          {Icons.chevron}
                        </button>
                      </div>
                      <div className={`${styles.mobileSubmenu} ${mobileSubmenu === item.name ? styles.open : ""}`}>
                        <Link
                          to={item.href}
                          className={styles.mobileSubmenuAll}
                          onClick={toggleMobile}
                        >
                          View all {item.name.toLowerCase()}
                          <span className={styles.mobileNavArrow}>{Icons.arrow}</span>
                        </Link>
                        {item.megaMenu.categories.map((cat, i) => (
                          <div key={i} className={styles.mobileSubmenuSection}>
                            <span className={styles.mobileSubmenuTitle}>{cat.title}</span>
                            {cat.items.map((subItem, j) => (
                              <Link key={j} to={subItem.href} className={styles.mobileSubmenuLink} onClick={toggleMobile}>
                                {subItem.icon && Icons[subItem.icon] && (
                                  <span className={styles.mobileSubmenuIcon} aria-hidden="true">
                                    {Icons[subItem.icon]}
                                  </span>
                                )}
                                <span className={styles.mobileSubmenuLabel}>{subItem.name}</span>
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link to={item.href} className={styles.mobileNavLink} onClick={toggleMobile}>
                      <span className={styles.mobileNavNumber}>0{index + 1}</span>
                      <span className={styles.mobileNavText}>{item.name}</span>
                      <span className={styles.mobileNavArrow}>{Icons.arrow}</span>
                    </Link>
                  )}
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
      <div className={styles.navSpacer} aria-hidden="true" />
    </>
  );
}